import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { GetLanguageService } from '../../services/translations/translations.service';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { PipesModule } from '../../pipes/pipes.module';
import { AuthService } from '../../services/auth/auth.service';
import { SharedStateService } from '../../services/shared-state/shared-state.service';
import { FormStateService } from '../../services/shared-form-state/form-state.service';
import { first } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { GenericAlertPopupComponent } from '../generic-alert-popup/generic-alert-popup.component';
import { SidebarService } from '../../services/sidebar/sidebar.service';
import { RouteEnum } from '../../enums/routes.enum';
import { LanguageSwitcherComponent } from '../language-switcher/language-switcher.component';
import { ChangePasswordModalComponent } from '../change-password-modal/change-password-modal.component';
import { isValidFunction } from '@pineapplelab/util';
import { environment } from '../../../environments/environment';
import { BreakpointObserver } from '@angular/cdk/layout';
import { SectionsEnum } from '../../enums/sections.enum';
import { SharedModule } from '../shared.module';
import { PermissionsEnum } from '../../enums/permissions.enum';
import { DirectiveModule } from '../../directives/directive.module';
import { ConfigService } from '../../services/config/config.service';
import { ProjectService } from '../../services/project/project.service';
import { RouterModule } from '@angular/router';
import { CartService } from '../../services/cart/cart.service';
import { Cart } from '../../models/cart';
import { UserService } from '../../services/user/user.service';
import { ToasterService } from '../../services/toaster/toaster.service';
import { ToasterTypeEnum } from '../../enums/toasterType.enum';
import { MaterialModule } from '../../material/material.module';
import { ChatComponent } from '../chat/chat.component';
@Component({
	selector: 'app-header',
	standalone: true,
	templateUrl: './header.component.html',
	styleUrls: ['./header.component.scss'],
	imports: [
		MatIconModule,
		MatButtonModule,
		FormsModule,
		MaterialModule,
		MatMenuModule,
		MatSelectModule,
		PipesModule,
		LanguageSwitcherComponent,
		ChangePasswordModalComponent,
		SharedModule,
		DirectiveModule,
		RouterModule,
		ChatComponent
	],
})
export class HeaderComponent implements OnInit, OnDestroy {

	@Input() showDefaultColor: boolean = false;

	private router = inject(Router);
	public getLanguageService = inject(GetLanguageService);
	public authService = inject(AuthService);
	public sharedStateService = inject(SharedStateService);
	private formStateService = inject(FormStateService);
	private dialog = inject(MatDialog);
	private sidebarService = inject(SidebarService);
	private route = inject(ActivatedRoute);
	private breakpointObserver = inject(BreakpointObserver);
	private configService = inject(ConfigService);
	private projectService = inject(ProjectService);
	private cartService = inject(CartService);
	private toasterService = inject(ToasterService);
	private userService = inject(UserService);
	private subscriptions: Subscription[] = [];

	user = this.authService.auth.user;
	createCategorySection: boolean = false;
	courseCategorySection: boolean = false;
	subscription: Subscription;
	categoryOrCourseData: any;
	isEditView: boolean = false;
	canSaveAsDraft: boolean;
	availableLanguages: string[] = [];
	categoryName: string;
	categoryId: string;
	isAddTranslation: boolean;
	isTranslation: boolean;
	routeEnum = RouteEnum;
	isSmallScreen: boolean = false;
	headerLogo: string;
	PermissionsEnum = PermissionsEnum;
	categoriesCount$: Observable<number>;

	ngOnDestroy(): void {
		this.subscriptions.forEach(sub => sub.unsubscribe());
	}

	async ngOnInit(): Promise<void> {
		this.initSubscriptions();
		this.checkCurrentPath(this.router.url);
		this.availableLanguages = this.getLanguageService._projectLanguages;
		this.headerLogo = this.configService.getConfig()?.headerLogo;
		this.categoriesCount$ = this.cartService.categoriesCount$;
		await this.cartService.loadCartAndCategories(this.authService.projectId);
	}

	initSubscriptions() {
		this.subscriptions.push(
			this.route.queryParams.subscribe(params => {
				this.categoryName = params['categoryName'];
				this.categoryId = params['categoryId'];
				this.isAddTranslation = params['isAddTranslation'] === 'true';
				this.isTranslation = params['isTranslation'] === 'true';
			}),

			this.sharedStateService.categoryOrCourseData.subscribe(section => {
				this.categoryOrCourseData = section;
			}),

			this.sharedStateService.contentEditView.subscribe(isEditView => {
				this.isEditView = isEditView;
			}),

			this.sharedStateService.canSaveAsDraftSubject.subscribe(canSaveAsDraftSubject => {
				this.canSaveAsDraft = canSaveAsDraftSubject;
			}),

			this.router.events.subscribe((e) => {
				if (e instanceof NavigationEnd) {
					this.checkCurrentPath(e.urlAfterRedirects);
					const isLeavingCreation = !e.urlAfterRedirects.includes('category-creation') && !e.urlAfterRedirects.includes('course-creation');
					if (isLeavingCreation) {
						this.resetHeaderData();
					}
				}
			}),

			this.breakpointObserver.observe(['(max-width: 768px)'])
				.subscribe(result => {
					this.isSmallScreen = result.matches;
				})
		);
	}

	resetHeaderData() {
		this.sharedStateService.categoryOrCourseData.next(null);
		this.sharedStateService.contentEditView.next(false);
		this.sharedStateService.canSaveAsDraftSubject.next(false);
	}

	checkScreenSize() {
		this.breakpointObserver.observe(['(max-width: 768px)'])
			.subscribe(result => {
				this.isSmallScreen = result.matches;
			});
	}

	checkCurrentPath(currentPath: string): void {
		if (currentPath.includes('category-creation')) {
			this.createCategorySection = true;
		} else {
			this.createCategorySection = false;
		}
		if (currentPath.includes('course-creation')) {
			this.courseCategorySection = true;
		} else {
			this.courseCategorySection = false;
		}
	}

	changeLanguage(language: string) {
		if (language) {
			this.getLanguageService.setLanguage(language);
		}
	}

	redirectToHome(path: string) {
		this.formStateService.formState$.pipe(
			first()
		).subscribe(state => {
			if (state && this.router.url.includes('course-creation')) {
				if (this.router.url.includes(`courseSection=${SectionsEnum.TRANSLATIONS}`) && this.isAddTranslation) {
					this.router.navigate([`/${RouteEnum.CATEGORY_CREATION}/${this.categoryId}/translations`]);
				} else if (this.router.url.includes(`isCreateCourse=true`)) {
					this.router.navigate([`/${RouteEnum.CATEGORY_CREATION}/${this.categoryId}`], { queryParams: { categorySection: SectionsEnum.COURSES } });
				} else {
					this.router.navigate([`/${RouteEnum.CATEGORY_CREATION}/${this.categoryId}`], { queryParams: { categorySection: SectionsEnum.TRANSLATIONS } });
				}
			} else {
				if (this.canSaveAsDraft) {
					this.sharedStateService.hasShownLeavePopup = true
					const dialogRef = this.dialog.open(GenericAlertPopupComponent, {
						minWidth: '30vw',
						maxWidth: '40vw',
						minHeight: '15vw',
						width: '30%',
						disableClose: true,
						data:
						{
							title: 'general.unsaved_changes_warning',
							description: 'general.progress_alert_description',
							acceptBtnText: this.isEditView ? 'general.save_an_leave' : 'general.save_as_draft',
							dismissBtnText: 'general.discard_changes',
							btnColor: 'primary'
						}
					});
					dialogRef.afterClosed().subscribe(async result => {
						if (result === null) {
							this.sharedStateService.resetShownLeavePopup();
							return;
						}
						if (result) {
							if (this.isEditView) {
								this.sharedStateService.triggerSaveEditedContent();
							} else {
								this.sharedStateService.triggerSaveAsDraft(false);
							}
						} else {
							if (isValidFunction(this.sharedStateService.discardedChangesCb)) {
								await this.sharedStateService.discardedChangesCb();
							}
							this.resetFormAndNavigate(path);
						}
					});
				} else {
					this.resetFormAndNavigate(path);
				}
			}
		});
	}

	redirectToUserDefaultPage() {
		const path = this.userService.getFirstAvailableRouteByRole(this.user?.role);
		this.redirectTo(path);
	}

	redirectTo(path: string) {
		this.router.navigateByUrl(path);
	}
	goToCart() {
		const isSignedIn = !!this.authService.auth?.user;
		const route = isSignedIn ? RouteEnum.SHOPPING_CART : `${RouteEnum.HOME}/${RouteEnum.SHOPPING_CART}`;
		this.router.navigateByUrl(route);
	}

	changePassword() {
		const dialogRef = this.dialog.open(ChangePasswordModalComponent, {
			minWidth: '30vw',
			maxWidth: this.isSmallScreen ? '95vw' : '40vw',
			width: '650px',
			minHeight: '15vw',
			data: {}
		})
	}

	resetFormAndNavigate(path: string): void {
		this.formStateService.clearState();
		this.sharedStateService.categoryOrCourseData.next(null);
		this.redirectTo(path);
	}

	isCategoryDataEmpty(): boolean {
		return !this.categoryOrCourseData || !this.categoryOrCourseData.categoryName || !this.categoryOrCourseData.idName;
	}

	logout() {
		this.authService.logout()
	}

	showSidebar() {
		this.sidebarService.toggleSidebar();
	}

	closeSidebar() {
		const screenWidth = window.innerWidth;
		if (screenWidth <= environment.minWidthResponsive) {
			this.sidebarService.closeSidebar();
		}
	}

	hasPermission(permission: PermissionsEnum): boolean {
		return this.projectService.hasPermissions(permission);
	}

	goToSettings(): void {
		this.router.navigateByUrl(RouteEnum.ACCOUNT_SETTINGS);
	}

	copyId(e: Event, id: string) {
		e.stopPropagation();
		navigator.clipboard.writeText(id)
		this.toasterService.show('general.copy_id', ToasterTypeEnum.SUCCESS);
	}

}
